import { API_URL } from "@/shared/utils/API_URL";
import { buildQuery } from "@/shared/utils/buildQuery";
import { useAuthStore } from "@/store/useAuthStore";
import axios from "axios";
import { UseData } from "./useData";
import useDateFilter from "./useDateFilter";
import useQueries from "./useQueries";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDebounce } from "./useDebounce";

interface LogData {
    action: "CREATE" | "UPDATE" | "DELETE" | 'PREPARE_PIECE' | 'FORWARD_OPTIMUS' | 'FORWARD_VELOX' | 'FORWARD_EXTERNAL';
    opname: string;
    title: string;
    refid?: number | string;
    ltype: string;
    ldata?: any;
    lnote?: string;
}

const PAGE_SIZE = 20;

interface UseLogsControllerProps {
    orderId?: string | number;
}

const useLogsController = ({ orderId }: UseLogsControllerProps = {}) => {
    const { t } = useTranslation();

    const queries = useQueries();
    const sessionId = useAuthStore((s) => s.sessionId);
    const licenceId = useAuthStore((s) => s.licenceId);
    const user = useAuthStore((s) => s.user);

    const { date, setDate, handleQuickSelect } = useDateFilter();

    const [selectedAction, setSelectedAction] = useState<string>('ALL');
    const [page, setPage] = useState(0);
    const [searchValue, setSearchValue] = useState('');

    const debouncedSearchValue = useDebounce(searchValue, 1000);


    useEffect(() => {
        setPage(0);
    }, [debouncedSearchValue, selectedAction, date.from, date.to]);

    const filters = useMemo(() => {
        const conditions: string[] = [];

        if (date.from) {
            const fromQuery = date.from.includes(' ') ? date.from : `${date.from} 00:00:00`;
            conditions.push(`tdate >= '${fromQuery}'`);
        }
        if (date.to) {
            const toQuery = date.to.includes(' ') ? date.to : `${date.to} 23:59:59`;
            conditions.push(`tdate <= '${toQuery}'`);
        }
        if (selectedAction && selectedAction !== 'ALL') {
            conditions.push(`action = '${selectedAction}'`);
        }
        if (debouncedSearchValue.trim()) {
            const s = debouncedSearchValue.trim();
            conditions.push(`(title LIKE '%${s}%' OR lnote LIKE '%${s}%' OR refid LIKE '%${s}%' OR ldata LIKE '%${s}%')`);
        }

        return conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : '';
    }, [date, selectedAction, debouncedSearchValue]);

    const totalLogsQuery = queries
        ? orderId
            ? buildQuery(queries.orderTrackingQuery, { order_id: orderId })
            : buildQuery(queries.allLogsQuery, {
                filters: filters
            })
        : '';

    const { data: totalLogsData, isLoading, isFetching } = UseData({
        q: totalLogsQuery,
        dependency: [filters, orderId],
    });

    const totalLogsList = totalLogsData?.data?.result || [];
    const totalLogsCount = totalLogsList.length;
    const totalPages = Math.max(1, Math.ceil(totalLogsCount / PAGE_SIZE));

    const startIndex = page * PAGE_SIZE;
    const pagedLogs = totalLogsList.slice(startIndex, startIndex + PAGE_SIZE);

    const log = async (logData: LogData) => {
        try {
            const now = new Date();
            const localISO = new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString();
            const orderDate = localISO.replace('T', ' ').substring(0, 19);

            const logQuery = buildQuery(queries?.insertLogQuery, {
                userid: 1,
                mac: user?.name,
                tdate: orderDate,
                action: logData.action,
                opname: logData.opname,
                title: logData.title,
                refid: logData.refid ? Number(logData.refid) : 0,
                ltype: logData.ltype,
                ldata: logData.ldata ? JSON.stringify(logData.ldata) : null,
                lnote: logData.lnote || "",
            });
            await axios.post(
                API_URL,
                { q: logQuery },
                {
                    params: {
                        t: "in",
                        id: licenceId,
                        s: sessionId,
                        btn: "w_itm",
                        api: "itqan",
                    },
                }
            );
            return true;
        } catch (error) {
            console.error("Logging Error:", error);
            return false;
        }
    };

    const handleExport = (format: 'pdf' | 'excel', customTitle = 'تقرير سجل الأنشطة ') => {
        if (!totalLogsQuery) {
            toast.error(t("no_query_available"));
            return;
        }

        const cleanQuery = totalLogsQuery
            .replace(/\s+LIMIT\s+\d+/gi, '')
            .replace(/\s+OFFSET\s+\d+/gi, '');

        const exportColumns = [
            { key: 'id', label: t('log_id') },
            { key: 'mac', label: t('user') },
            { key: 'action', label: t('action') },
            { key: 'opname', label: t('operation') },
            { key: 'title', label: t('description') },
            { key: 'ltype', label: t('type') },
            { key: 'tdate', label: t('date_time') },
            { key: 'lnote', label: t('note') },
        ];

        const exportUrl = new URL("https://w-ord.ntelgnt.com/export.php");

        exportUrl.searchParams.append("format", format);
        exportUrl.searchParams.append("title", customTitle);
        exportUrl.searchParams.append("id", licenceId || "");
        exportUrl.searchParams.append("s", sessionId || "");

        const form = document.createElement("form");
        form.method = "POST";
        form.action = exportUrl.toString();
        form.target = "_blank";

        const qInput = document.createElement("input");
        qInput.type = "hidden";
        qInput.name = "q";
        qInput.value = cleanQuery;
        form.appendChild(qInput);

        const columnsInput = document.createElement("input");
        columnsInput.type = "hidden";
        columnsInput.name = "columns";
        columnsInput.value = JSON.stringify(exportColumns);
        form.appendChild(columnsInput);

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };

    const isSearchPending = searchValue !== debouncedSearchValue;
    const isSearching = isSearchPending || isFetching;

    return {
        log,
        logs: pagedLogs,
        setDate,
        handleQuickSelect,
        isLoading,
        date,
        selectedAction,
        setSelectedAction,
        page,
        setPage,
        totalPages,
        pageSize: PAGE_SIZE,
        totalLogsCount,
        searchValue,
        setSearchValue,
        handleExport, isSearching
    };
};

export default useLogsController;